import { useEffect, useState } from 'react';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';

import type { CloudId } from '@atlassian/jira-shared-types/src/general.tsx';

type UseLicenseInformationProps = {
	cloudId: CloudId;
};

type LicenseInformationResponse = {
	products: {
		[key: string]: {
			state?: string;
			edition?: string;
			ccpEntitlementId?: string;
			ccpTransactionAccountId?: string;
			billingPeriod?: string;
		};
	};
};

export const useLicenseInformation = ({ cloudId }: UseLicenseInformationProps) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	const [data, setData] = useState<LicenseInformationResponse | null>(null);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			try {
				const targetEdition = await performGetRequest<LicenseInformationResponse>(
					`/gateway/api/xflow/${cloudId}/license-information`,
				);

				setData(targetEdition);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setError(err);
			}
			setLoading(false);
		};

		fetchData();
	}, [cloudId]);

	return {
		loading,
		error,
		data,
	};
};

type GetFromLicenseProps = {
	licenseInformation: LicenseInformationResponse | null;
	productKey: string;
};

export const getCcpEntitlementIdFromLicense = ({
	licenseInformation,
	productKey,
}: GetFromLicenseProps) => {
	const product = licenseInformation?.products[productKey];
	return product?.ccpEntitlementId || null;
};

export const getCcpTransactionAccountIdFromLicense = ({
	licenseInformation,
	productKey,
}: GetFromLicenseProps) => {
	const product = licenseInformation?.products[productKey];
	return product?.ccpTransactionAccountId || null;
};

export const getCurrentEditionFromLicense = ({
	licenseInformation,
	productKey,
}: GetFromLicenseProps) => {
	const product = licenseInformation?.products[productKey];
	return product?.edition || null;
};
